import { Link } from "react-router-dom";
import {
  Home, ShoppingCart, Repeat, Users, PieChart, Mail, UserPlus, ArrowRight,
  Check, Lock, Plane,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

/* Family Expense Hub — product page for everyday household spending (Feb-2026
   unbundling). Runs on the general Expense Manager (/expenses) with Household scope. */

const FEATURES = [
  { icon: ShoppingCart, color: "text-amber-700", title: "Everyday categories",
    text: "Groceries, utilities, rent & mortgage, school, health, subscriptions — 17 categories plus your own custom ones." },
  { icon: Users, color: "text-blue-700", title: "Shared with your household",
    text: "Everyone in the household logs under their own name. Switch between 'Me' and 'Household' views any time." },
  { icon: Repeat, color: "text-violet-700", title: "Recurring bills, handled",
    text: "Mark rent, subscriptions and utilities as monthly — they auto-appear each month, and a spend recap email arrives on the 1st." },
  { icon: PieChart, color: "text-emerald-700", title: "See where it goes",
    text: "Month-by-month navigation, category breakdowns and AI insights that spot patterns and savings opportunities." },
  { icon: Plane, color: "text-cyan-700", title: "Travel stays separate",
    text: "Trip spending lives in the Trip Expense Manager — it appears in its own clearly-marked section, never mixed into everyday money." },
  { icon: Lock, color: "text-rose-600", title: "Strictly private",
    text: "Household finances never appear in any travel feed, shared trip or public page. Only your household members ever see them." },
];

export default function FamilyExpenseHubPage() {
  const { user } = useAuth();
  return (
    <div data-testid="family-expense-page">
      <section className="relative border-b border-zinc-200 bg-amber-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-300 mb-3 flex items-center gap-2"><Home className="w-4 h-4" /> Family Expense Hub — by TravelerPulse</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="family-expense-title">
            The household's money,<br />on the same page.
          </h1>
          <p className="text-white/85 text-lg mt-4 max-w-2xl">
            Groceries, bills, rent and everything in between — logged by the whole household,
            budgeted monthly, and kept completely separate from your travel life.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to={user ? "/expenses" : "/register"} data-testid="family-expense-cta"
              className="inline-flex items-center gap-2 bg-amber-500 text-zinc-900 px-7 py-3.5 font-700 hover:bg-amber-400 transition-colors">
              <UserPlus className="w-5 h-5" /> {user ? "Open my expenses" : "Start free — 45 days, no card"}
            </Link>
            <Link to="/trip-expense-manager" data-testid="family-expense-trip-link"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-7 py-3.5 font-700 hover:bg-white/20 transition-colors">
              Tracking a trip instead? <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="family-expense-features">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-8">Simple enough to actually keep up</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-zinc-300 bg-white p-5">
              <f.icon className={`w-7 h-7 ${f.color}`} />
              <div className="font-heading font-900 text-lg mt-3">{f.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="family-expense-how">
          <h2 className="font-heading font-900 text-2xl mb-5">Up and running in minutes</h2>
          <ol className="space-y-3 text-sm text-zinc-700">
            {[
              "Add your household members — each keeps their own login and privacy.",
              "Set a monthly budget (optional) and pick the categories your family uses.",
              "Log spending as it happens — a few seconds per entry, from any device.",
              "On the 1st of each month, get the recap email: totals, categories and how you did against budget.",
            ].map((s, i) => (
              <li key={i} className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-amber-600 text-white text-xs font-800 flex items-center justify-center">{i + 1}</span>{s}</li>
            ))}
          </ol>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={user ? "/expenses" : "/register"} data-testid="family-expense-bottom-cta"
              className="inline-flex items-center gap-2 bg-amber-600 text-white px-6 py-3 font-700 hover:bg-amber-500 transition-colors">
              {user ? "Start logging expenses" : "Create your free account"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/travel-hub" className="inline-flex items-center gap-2 border border-zinc-300 px-6 py-3 font-700 text-zinc-700 hover:border-primary hover:text-primary transition-colors">
              <Mail className="w-4 h-4" /> Explore My Travel Hub
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Part of the SocialPulse family of tools — one account works across the network.</p>
        </div>
      </section>
    </div>
  );
}
